import { forwardRef } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "@/lib/cn";

/**
 * Switch — Radix toggle (Faza 8 polish — token-aware + size variant).
 *
 * Sizes:
 *   - sm: h-4 w-7  (kompakt list rows)
 *   - md: h-5 w-9  (default — settings rows)
 *   - lg: h-6 w-11 (prominent — danger toggle)
 *
 * ON state — Aurora gradient (--switch-on-bg) + soft glow.
 * OFF state — --switch-off-bg (subtle dark).
 */
type SwitchSize = "sm" | "md" | "lg";

const SIZE_MAP: Record<SwitchSize, { root: string; thumb: string; translate: string }> = {
  sm: { root: "h-4 w-7", thumb: "h-3 w-3", translate: "data-[state=checked]:translate-x-3.5 data-[state=unchecked]:translate-x-0.5" },
  md: { root: "h-5 w-9", thumb: "h-4 w-4", translate: "data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-0.5" },
  lg: { root: "h-6 w-11", thumb: "h-5 w-5", translate: "data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0.5" },
};

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  size?: SwitchSize;
}

export const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, size = "md", style, ...props }, ref) => {
  const cfg = SIZE_MAP[size];
  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border border-transparent",
        "transition-all duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        cfg.root,
        "data-[state=unchecked]:bg-[var(--switch-off-bg)]",
        "data-[state=checked]:[background:var(--switch-on-bg)] data-[state=checked]:shadow-[var(--switch-on-glow)]",
        className,
      )}
      style={style}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block rounded-full bg-[var(--switch-thumb)] shadow-[var(--switch-thumb-shadow)]",
          "transition-transform duration-200 ease-out",
          cfg.thumb,
          cfg.translate,
        )}
      />
    </SwitchPrimitive.Root>
  );
});
Switch.displayName = "Switch";
